import React, {useEffect, useState} from 'react'
import {useMutation} from "@tanstack/react-query";
import {useAuth} from "../../providers/AuthProvider";
import {Alert, Button, Typography} from 'antd';

import {
    CheckCircleFilled,
    CodeOutlined,
    ExclamationCircleFilled,
    FileTextOutlined,
    LoadingOutlined,
    RobotOutlined,
    StarFilled,
    UploadOutlined
} from '@ant-design/icons';
import SpinLoader from "../../components/SpinLoader";
import ResumeImportSelection from "./components/ResumeImportSelection";
import User from "../../models/User";

const { Title, Text, Paragraph } = Typography;

const ResumeUploadModal = (props) => {

    const {auth, talentUpdate, update: updateAuthProps, isUpdating} = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [hasInvalidItems, setHasInvalidItems] = useState(false);
    const [hasImported, setHasImported] = useState(auth.canImportResumeItemsForApplication())

    const importResumeDataMutation = useMutation(['importResume'], async() => {

        await setIsLoading(true)

        if (auth.generated_account_data) {
            if (!User.getResumeImportsForUser(auth.id)){
                await auth.syncResumeImportLocalStorage(response.success.data.generated_account_data);
            }
            await TB.wait(2000);
            return auth.generated_account_data
        }
        let request = TB.request(TB.api_endpoint('/api/sessions/update/profile/resume'));

        let response = await request.post().catch(console.warn);

        if (!response?.success?.data?.generated_account_data) {
            throw new Error('Something went wrong!');
        }

        let user = TB.auth().user()

        user.generated_account_data = response.success.data.generated_account_data;

        await TB.auth().update(user);

        return response.success.data.generated_account_data;
    }, {
        onError: (error) => {
            console.log(error);
            return setHasInvalidItems(true);
        }
    })

    const importResume = async () => {
        let data = await importResumeDataMutation.mutateAsync({}).catch(console.warn);

        await setHasInvalidItems(!auth.canImportResumeItemsForApplication(data))

        return setHasImported(auth.canImportResumeItemsForApplication(data))
    }


    return (
        <SpinLoader isLoading={isUpdating} className="resume-container pb-3">
            <div className="py-3">

                <p className="text-lg font-medium">
                    Import data from your resume <small>(optional, but recommended)</small>
                </p>

            </div>
            {!(importResumeDataMutation.isLoading || isLoading) && !hasImported && (
                <React.Fragment>
                    {hasInvalidItems ? (
                        <EmptyResumeImportFeedback>
                            <div className="flex flex-col items-center">
                                <Button
                                    onClick={props.onRequestClose}
                                    className="hover:text-gray-700 mt-3"
                                >
                                    Continue with {auth.isApplicationApproved() ? 'profile' : 'application'}
                                </Button>
                            </div>
                        </EmptyResumeImportFeedback>
                    ): <AIResumeParserPrompt onAccept={importResume} onDecline={props.onRequestClose} />}


                </React.Fragment>
            )}
            {(importResumeDataMutation.isLoading || isLoading) && (
                <AIResumeParsingLoading onFinishAnimation={async () => {
                    try {
                        await TB.wait(1000);
                        setIsLoading(false)
                    }catch (e) {
                        console.error(e);
                    }
                }} />
            )}
            {!(importResumeDataMutation.isLoading || isLoading) && hasImported && (
                <React.Fragment>
                    <div className="resume-instructions-header pb-3">
                        {TB.flatten(TB.values((auth.generated_account_data || {}))).length > 0 && (
                            <p className="mt-2">
                            Your resume was uploaded successfully.&nbsp;
                                {!hasInvalidItems && "See below to view what we were able to import.\n"}
                                Check which items you would like to autofill with your profile. Note: You can edit these items later after importing.
                            </p>
                        )}
                        {hasInvalidItems && (
                            <div className="alert alert-danger mt-2 border border-danger">
                                <p>
                                    Note: We are not able to import all items from your resume due to missing
                                    information (items with missing information are unchecked below). Confirm
                                    which items you want to import and click
                                    the <b>Import Resume Data</b> button at the bottom of this page to continue.
                                </p>
                            </div>
                        )}
                    </div>

                    <ResumeImportSelection onSubmit={props.onUpdate} isDisabled={props.disabled}/>

                </React.Fragment>
            )}


        </SpinLoader>
    )
};


const AIResumeParserPrompt = ({ onAccept, onDecline }) => {
    return (
        <div className="w-full max-w-2xl mx-auto shadow-sm">
            <div className="p-8">
                {/* Header Section */}
                <div className="flex items-center justify-center gap-3 mb-8">
                    <div className="h-12 w-12 flex items-center justify-center">
                        <img src="/images/wrex.png" className="w-12 h-12" />
                    </div>
                    <div className="text-center">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-1 mt-0 mx-0">
                            Let AI Do the Heavy Lifting
                        </h2>
                        <p className="text-gray-600 m-0">
                            Save time by letting <b>Wrex</b>, Wripple's AI assistant, fill your profile
                        </p>
                    </div>
                </div>

                {/* Process Visualization */}
                <div className="flex justify-between items-center mb-8 px-4">
                    {/* Step 1 */}
                    <div className="flex flex-col items-center text-center w-1/3">
                        <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center mb-3">
                            <FileTextOutlined className="text-2xl text-blue-600" />
                        </div>
                        <span className="text-sm text-gray-600">Your Resume</span>
                    </div>

                    {/* Arrow/Process */}
                    <div className="flex-1 flex items-center justify-center">
                        <div className="h-8 flex items-center">
                            <StarFilled className="text-amber-500 text-lg animate-pulse" />
                            <div className="h-0.5 w-16 bg-amber-200"></div>
                            <StarFilled className="text-amber-500 text-lg animate-pulse" />
                        </div>
                    </div>

                    {/* Step 2 */}
                    <div className="flex flex-col items-center text-center w-1/3">
                        <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-3">
                            <UploadOutlined className="text-2xl text-green-600" />
                        </div>
                        <span className="text-sm text-gray-600">Auto-Filled Profile</span>
                    </div>
                </div>

                {/* Benefits List */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <h3 className="font-medium text-gray-900 mb-2 m-0">What you'll get:</h3>
                    <ul className="space-y-2 m-0 list-none">
                        <li className="flex items-center gap-2 text-gray-600">
                            <div className="h-1.5 w-1.5 rounded-full bg-purple-500"></div>
                            Instant profile completion with AI-powered accuracy
                        </li>
                        <li className="flex items-center gap-2 text-gray-600">
                            <div className="h-1.5 w-1.5 rounded-full bg-purple-500"></div>
                            Smart extraction of skills, experience, and expertise
                        </li>
                        <li className="flex items-center gap-2 text-gray-600">
                            <div className="h-1.5 w-1.5 rounded-full bg-purple-500"></div>
                            Review and confirm everything before saving
                        </li>
                    </ul>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 justify-end">
                    <Button
                        onClick={onDecline}
                        className="hover:text-gray-700"
                    >
                        I'll Fill It Manually
                    </Button>
                    <Button
                        onClick={onAccept}
                        className="bg-purple-600 text-white hover:bg-purple-700 border-purple-600 hover:border-purple-700"
                    >
                        Yes, Use AI to Help
                    </Button>
                </div>
            </div>

            <style jsx={true} global={true}>{`
        .ant-card {
          border-radius: 0.5rem;
        }
        .ant-card-body {
          padding: 0;
        }
        .ant-btn:hover {
          border-color: #e5e7eb;
          color: inherit;
        }
        .ant-btn-primary {
          background: #9333ea;
        }
        .ant-btn-primary:hover {
          background: #7e22ce !important;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
        </div>
    );
};

const AIResumeParsingLoading = (props) => {
    const [currentStep, setCurrentStep] = useState(0);

    const steps = [
        {
            id: 0,
            icon: FileTextOutlined,
            label: 'Your Resume',
            bgColor: 'bg-blue-100',
            iconColor: 'text-blue-600',
            status: 'Document received'
        },
        {
            id: 1,
            icon: RobotOutlined,
            label: 'AI Analysis',
            bgColor: 'bg-purple-100',
            iconColor: 'text-purple-600',
            status: 'Analyzing content structure'
        },
        {
            id: 2,
            icon: CodeOutlined,
            label: 'Processing',
            bgColor: 'bg-amber-100',
            iconColor: 'text-amber-600',
            status: 'Extracting professional experience'
        },
        {
            id: 3,
            icon: CheckCircleFilled,
            label: 'Complete',
            bgColor: 'bg-green-100',
            iconColor: 'text-green-600',
            status: 'Mapping skills and qualifications'
        }
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentStep((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
        }, 8000);

        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (currentStep === 3) {
            props.onFinishAnimation()
        }
    }, [currentStep]);
    const getStepStatus = (stepId) => {
        if (stepId < currentStep) return 'completed';
        if (stepId === currentStep) return 'current';
        return 'pending';
    };

    return (
        <React.Fragment>
            <div className="p-8">
                {/* Header */}
                <div className="flex items-center justify-center gap-3 mb-8">
                    <div className="h-12 w-12 flex items-center justify-center">
                        {/*<RobotOutlined className="text-lg text-purple-600" />*/}
                        <img src="/images/wrex.png" className="w-12 h-12" />
                    </div>
                    <div className="text-center">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-1 mt-0 mx-0">
                            AI Magic in Progress
                        </h2>
                        <p className="text-gray-600 m-0">
                            <p className="text-gray-600">
                                <b>Wrex</b>, Wripple's AI assistant, is carefully reviewing your resume...
                            </p>
                        </p>
                    </div>
                </div>


                {/* Processing Animation */}
                <div className="relative mb-12 h-24">
                    {/* Progress Line */}
                    <div className="absolute top-8 left-0 w-full h-0.5 bg-gray-100">
                        <div className="relative h-full">
                            <div
                                className="absolute top-0 left-0 h-full bg-purple-600 transition-all duration-1000"
                                style={{ width: `calc(${(currentStep / (steps.length - 1)) * 100}% - ${(currentStep > 0 || currentStep < 3) ?(((currentStep - 1) * 64) + 16) : 0}px)` }}
                            />
                            <div
                                className="absolute -top-2 transform transition-all duration-1000"
                                style={{ left: `calc(${(currentStep / (steps.length - 1)) * 100}% - ${(currentStep > 0 || currentStep < 3) ?(((currentStep - 1) * 64) + 16) : 0}px)` }}
                            >
                                <StarFilled className="text-sm text-purple-600 animate-spin" />
                            </div>
                        </div>
                    </div>

                    <div className="absolute inset-0 flex justify-between items-center">
                        {steps.map((step, index) => {
                            const status = getStepStatus(index);
                            const Icon = step.icon;

                            return (
                                <div key={step.id} className="relative flex flex-col items-center space-y-2 w-1/4">

                                    <div className="h-16 w-16 rounded-lg flex items-stretch bg-white justify-center">
                                        <div
                                            className={`h-16 w-16 rounded-lg flex items-center justify-center
                      ${step.bgColor}
                      ${status === 'current' ? 'animate-pulse' : ''}
                      ${status === 'pending' ? 'opacity-50' : ''}`}
                                        >
                                            <Icon className={`text-2xl ${step.iconColor}`}/>
                                        </div>
                                    </div>

                                    <span
                                        className={`text-sm ${status === 'pending' ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {step.label}
                                    </span>

                                </div>
                            );
                        })}
                    </div>


                </div>

                {/* Status Messages */}
                <div className="space-y-3 max-w-md mx-auto mt-8">
                    {steps.map((step, index) => {
                        const status = getStepStatus(index);

                        return (
                            <div key={step.id} className="flex items-center space-x-3">
                                {status === 'completed' && (
                                    <CheckCircleFilled className="text-lg text-green-500" />
                                )}
                                {status === 'current' && (
                                    <LoadingOutlined className="text-lg text-purple-600 animate-spin" />
                                )}
                                {status === 'pending' && (
                                    <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                                )}
                                <span
                                    className={
                                        status === 'completed' ? 'text-gray-600' :
                                            status === 'current' ? 'text-purple-600 font-medium' :
                                                'text-gray-400'
                                    }
                                >
                                    {step.status}
                                </span>
                            </div>
                        );
                    })}
                </div>

                {/* Footer Message */}
                <div className="text-center mt-8 text-sm text-gray-500">
                    {currentStep === steps.length - 1 ? (
                        "Almost done! Preparing your profile..."
                    ) : (
                        "This usually takes less than a minute. Please don't close this window."
                    )}
                </div>
            </div>

            <style jsx global>{`
        .ant-card {
          border-radius: 0.5rem;
        }
        .ant-card-body {
          padding: 0;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 2s linear infinite;
        }
      `}</style>
        </React.Fragment>
    );
};

const EmptyResumeImportFeedback = (props) => {

    const description = (
        <div className="text-amber-700">
            <p className="mb-3">
                We couldn't extract any information from the uploaded resume. This might happen because:
            </p>
            <ul className="list-disc pl-5 space-y-1">
                <li>The file format isn't supported</li>
                <li>The document is password protected</li>
                <li>The resume's content structure isn't recognized</li>
                <li>The file isn't corrupted or password protected</li>
            </ul>
            {/*<div className="mt-4 space-y-2">*/}
            {/*    <p className="font-medium">Try these solutions:</p>*/}
            {/*    <ul className="list-disc pl-5 space-y-1">*/}
            {/*        <li>Upload a PDF or Word document (.doc, .docx)</li>*/}
            {/*        <li>Make sure the file isn't corrupted or password protected</li>*/}
            {/*        <li>Ensure the resume contains text and not just images</li>*/}
            {/*    </ul>*/}
            {/*</div>*/}

        </div>
    );

    return (
        <div className="w-full max-w-lg mx-auto p-4">
            <style jsx global>{`
        .custom-alert.ant-alert {
          background-color: rgb(254 243 199);
          border-color: rgb(252 211 77);
        }
        .custom-alert .ant-alert-message {
          color: rgb(146 64 14);
          font-weight: 500;
        }
        .custom-alert .ant-alert-description {
          color: rgb(180 83 9);
        }
        .custom-alert .anticon {
          color: rgb(217 119 6);
          font-size: 20px;
        }
      `}</style>
            <Alert
                className="custom-alert"
                message="No Resume Data Found"
                description={description}
                type="warning"
                showIcon
                icon={<ExclamationCircleFilled />}
            />
            {props.children}
        </div>
    );
};

const styles = {
    fieldSection: {
        flexDirection: 'row'
    },
    field: {
        padding: '0 15px',
        marginLeft: 'auto',
        marginRight: 'auto'
    },
};

export default ResumeUploadModal